import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import usePageTitle from '../hooks/usePageTitle';
import api from '../utils/api';
import toast from 'react-hot-toast';
import Modal from '../components/Modal';
import ApiDocumentation from '../components/ApiDocumentation';
import AccessManagement from '../components/AccessManagement';
import {
  CloudArrowUpIcon,
  FolderIcon,
  InformationCircleIcon,
  CheckCircleIcon,
  DocumentTextIcon,
  Cog6ToothIcon,
  KeyIcon,
  BookOpenIcon,
  UsersIcon,
} from '@heroicons/react/24/outline';

const Settings = () => {
  usePageTitle('Settings');
  const { user } = useAuth();
  const [loadingAP, setLoadingAP] = useState(false);
  const [loadingAR, setLoadingAR] = useState(false);
  const [loadingCustomerContracts, setLoadingCustomerContracts] = useState(false);
  const [loadingServiceAccount, setLoadingServiceAccount] = useState(false);
  const [showPrivateKey, setShowPrivateKey] = useState(false);
  const [serviceAccountModalOpen, setServiceAccountModalOpen] = useState(false);
  const [apFolderModalOpen, setApFolderModalOpen] = useState(false);
  const [arFolderModalOpen, setArFolderModalOpen] = useState(false);
  const [customerContractsFolderModalOpen, setCustomerContractsFolderModalOpen] = useState(false);
  const [settings, setSettings] = useState({
    google_drive_ap_folder_id: '',
    google_drive_ar_folder_id: '',
    google_drive_customer_contracts_folder_id: '',
    google_service_account_email: '',
    google_private_key: '',
  });
  const [activeTab, setActiveTab] = useState('google-drive');

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await api.get('/settings');

      // Extract values from the settings object structure
      const settingsData = {
        google_drive_ap_folder_id: response.data.google_drive_ap_folder_id?.value || '',
        google_drive_ar_folder_id: response.data.google_drive_ar_folder_id?.value || '',
        google_drive_customer_contracts_folder_id: response.data.google_drive_customer_contracts_folder_id?.value || '',
        google_service_account_email: response.data.google_service_account_email?.value || '',
        google_private_key: response.data.google_private_key?.value || '',
      };

      setSettings(settingsData);
    } catch (error) {
      console.error('Error fetching settings:', error);
    }
  };

  const handleSaveAP = async (e) => {
    e.preventDefault();
    setLoadingAP(true);

    try {
      await api.post('/settings', {
        setting_key: 'google_drive_ap_folder_id',
        setting_value: settings.google_drive_ap_folder_id,
        description: 'Google Drive folder ID for AP invoices'
      });
      toast.success('AP folder configuration saved successfully');
      setApFolderModalOpen(false);
    } catch (error) {
      console.error('Error saving AP folder:', error);
      toast.error('Failed to save AP folder configuration');
    } finally {
      setLoadingAP(false);
    }
  };

  const handleSaveAR = async (e) => {
    e.preventDefault();
    setLoadingAR(true);

    try {
      await api.post('/settings', {
        setting_key: 'google_drive_ar_folder_id',
        setting_value: settings.google_drive_ar_folder_id,
        description: 'Google Drive folder ID for AR invoices'
      });
      toast.success('AR folder configuration saved successfully');
      setArFolderModalOpen(false);
    } catch (error) {
      console.error('Error saving AR folder:', error);
      toast.error('Failed to save AR folder configuration');
    } finally {
      setLoadingAR(false);
    }
  };

  const handleSaveCustomerContracts = async (e) => {
    e.preventDefault();
    setLoadingCustomerContracts(true);

    try {
      await api.post('/settings', {
        setting_key: 'google_drive_customer_contracts_folder_id',
        setting_value: settings.google_drive_customer_contracts_folder_id,
        description: 'Google Drive folder ID for customer contracts'
      });
      toast.success('Customer contracts folder configuration saved successfully');
      setCustomerContractsFolderModalOpen(false);
    } catch (error) {
      console.error('Error saving customer contracts folder:', error);
      toast.error('Failed to save customer contracts folder configuration');
    } finally {
      setLoadingCustomerContracts(false);
    }
  };

  const handleSaveServiceAccount = async (e) => {
    e.preventDefault();
    setLoadingServiceAccount(true);

    try {
      const settingsToSave = [
        {
          setting_key: 'google_service_account_email',
          setting_value: settings.google_service_account_email,
          description: 'Google service account email for Drive access'
        },
        {
          setting_key: 'google_private_key',
          setting_value: settings.google_private_key,
          description: 'Google service account private key'
        }
      ];

      await api.post('/settings/batch', { settings: settingsToSave });
      toast.success('Service account credentials saved successfully');
      setServiceAccountModalOpen(false);
    } catch (error) {
      console.error('Error saving service account credentials:', error);
      toast.error('Failed to save service account credentials');
    } finally {
      setLoadingServiceAccount(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setSettings(prev => ({
      ...prev,
      [name]: value
    }));
  };

  if (user?.role !== 'admin') {
    return (
      <div className="page-container">
        <div className="page-header">
          <h1>Access Denied</h1>
        </div>
        <div className="card">
          <p>Only administrators can access system settings.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container">
      <div className="page-header">
        <div>
          <h1>System Settings</h1>
          <p className="page-subtitle">Configure system-wide settings and integrations</p>
        </div>
      </div>

      {/* Settings Tabs */}
      <div className="settings-tabs">
        <button
          className={`settings-tab ${activeTab === 'google-drive' ? 'active' : ''}`}
          onClick={() => setActiveTab('google-drive')}
        >
          <CloudArrowUpIcon style={{ width: 20, height: 20 }} />
          Google Drive Integration
        </button>
        <button
          className={`settings-tab ${activeTab === 'api-docs' ? 'active' : ''}`}
          onClick={() => setActiveTab('api-docs')}
        >
          <BookOpenIcon style={{ width: 20, height: 20 }} />
          API Documentation
        </button>
        <button
          className={`settings-tab ${activeTab === 'access-management' ? 'active' : ''}`}
          onClick={() => setActiveTab('access-management')}
        >
          <UsersIcon style={{ width: 20, height: 20 }} />
          Access Management
        </button>
      </div>

      {/* Google Drive Settings */}
      {activeTab === 'google-drive' && (
        <div className="settings-content">
          {/* Configuration Cards */}
          <div className="config-cards-grid">
            <div className="config-card">
              <div className="config-card-icon">
                <KeyIcon style={{ width: 32, height: 32 }} />
              </div>
              <h3>Service Account Credentials</h3>
              <p>Configure Google Cloud service account email and private key</p>
              <button
                className="btn btn-primary"
                onClick={() => setServiceAccountModalOpen(true)}
              >
                <Cog6ToothIcon style={{ width: 18, height: 18 }} />
                Configure Credentials
              </button>
              {settings.google_service_account_email && (
                <small className="config-status">Configured: {settings.google_service_account_email}</small>
              )}
            </div>

            <div className="config-card">
              <div className="config-card-icon">
                <FolderIcon style={{ width: 32, height: 32 }} />
              </div>
              <h3>AP Invoices Folder</h3>
              <p>Set Google Drive folder for Accounts Payable invoices</p>
              <button
                className="btn btn-primary"
                onClick={() => setApFolderModalOpen(true)}
              >
                <Cog6ToothIcon style={{ width: 18, height: 18 }} />
                Configure AP Folder
              </button>
              {settings.google_drive_ap_folder_id && (
                <small className="config-status">Configured</small>
              )}
            </div>

            <div className="config-card">
              <div className="config-card-icon">
                <FolderIcon style={{ width: 32, height: 32 }} />
              </div>
              <h3>AR Invoices Folder</h3>
              <p>Set Google Drive folder for Accounts Receivable invoices</p>
              <button
                className="btn btn-primary"
                onClick={() => setArFolderModalOpen(true)}
              >
                <Cog6ToothIcon style={{ width: 18, height: 18 }} />
                Configure AR Folder
              </button>
              {settings.google_drive_ar_folder_id && (
                <small className="config-status">Configured</small>
              )}
            </div>

            <div className="config-card">
              <div className="config-card-icon">
                <FolderIcon style={{ width: 32, height: 32 }} />
              </div>
              <h3>Customer Contracts Folder</h3>
              <p>Set Google Drive folder for customer contracts (subfolders created per customer)</p>
              <button
                className="btn btn-primary"
                onClick={() => setCustomerContractsFolderModalOpen(true)}
              >
                <Cog6ToothIcon style={{ width: 18, height: 18 }} />
                Configure Contracts Folder
              </button>
              {settings.google_drive_customer_contracts_folder_id && (
                <small className="config-status">Configured</small>
              )}
            </div>
          </div>

          {/* Service Account Modal */}
          <Modal
            isOpen={serviceAccountModalOpen}
            onClose={() => setServiceAccountModalOpen(false)}
            title="Service Account Credentials"
            size="lg"
          >
            <form onSubmit={handleSaveServiceAccount}>
                <div className="alert alert-info" style={{ marginBottom: 'var(--space-lg)' }}>
                  <InformationCircleIcon style={{ width: 20, height: 20, display: 'inline', marginRight: 8 }} />
                  <div>
                    <strong>Security Note:</strong> These credentials are stored securely in the database.
                    Ensure your database is properly secured and only accessible by authorized personnel.
                  </div>
                </div>

                <div className="form-section">
                  <div className="form-group">
                    <label htmlFor="google_service_account_email">
                      Service Account Email
                    </label>
                    <small className="form-text" style={{ display: 'block', marginBottom: '12px' }}>
                      Enter the service account email from your Google Cloud JSON key file (the <code>client_email</code> field).
                    </small>
                    <textarea
                      id="google_service_account_email"
                      name="google_service_account_email"
                      className="form-control"
                      value={settings.google_service_account_email}
                      onChange={handleInputChange}
                      placeholder="service-account@project-id.iam.gserviceaccount.com"
                      rows="2"
                      style={{
                        fontFamily: 'monospace',
                        fontSize: '0.9rem',
                        resize: 'vertical',
                        width: '100%'
                      }}
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="google_private_key">
                      Private Key
                    </label>
                    <small className="form-text" style={{ display: 'block', marginBottom: '12px' }}>
                      Paste the entire private key from your JSON key file (the <code>private_key</code> field).
                      Include the BEGIN and END lines. Line breaks will be handled automatically.
                    </small>
                    <div style={{ marginBottom: '12px' }}>
                      <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', fontWeight: 'normal' }}>
                        <input
                          type="checkbox"
                          checked={showPrivateKey}
                          onChange={(e) => setShowPrivateKey(e.target.checked)}
                          style={{ marginRight: '8px', cursor: 'pointer' }}
                        />
                        Show private key
                      </label>
                    </div>
                    <textarea
                      id="google_private_key"
                      name="google_private_key"
                      className="form-control"
                      value={settings.google_private_key}
                      onChange={handleInputChange}
                      placeholder="-----BEGIN PRIVATE KEY-----&#10;Your private key content here&#10;-----END PRIVATE KEY-----"
                      rows="16"
                      style={{
                        fontFamily: 'monospace',
                        fontSize: '0.85rem',
                        resize: 'vertical',
                        width: '100%',
                        WebkitTextSecurity: showPrivateKey ? 'none' : 'disc'
                      }}
                      required
                    />
                  </div>
                </div>

              <div className="form-actions">
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={loadingServiceAccount}
                >
                  {loadingServiceAccount ? 'Saving...' : 'Apply Service Account Credentials'}
                </button>
              </div>
            </form>
          </Modal>

          {/* AP Invoices Folder Modal */}
          <Modal
            isOpen={apFolderModalOpen}
            onClose={() => setApFolderModalOpen(false)}
            title="AP Invoices Folder Configuration"
          >
            <form onSubmit={handleSaveAP}>
                <div className="form-section">
                  <div className="form-group">
                    <label htmlFor="google_drive_ap_folder_id">
                      <FolderIcon style={{ width: 18, height: 18, display: 'inline', marginRight: 8 }} />
                      AP Invoices Folder ID
                    </label>
                    <small className="form-text" style={{ display: 'block', marginBottom: '12px' }}>
                      Paste the Google Drive folder ID where AP invoice documents will be stored.
                      You can find this ID in the folder's URL after /folders/
                    </small>
                    <textarea
                      id="google_drive_ap_folder_id"
                      name="google_drive_ap_folder_id"
                      className="form-control"
                      value={settings.google_drive_ap_folder_id}
                      onChange={handleInputChange}
                      placeholder="e.g., 1a2b3c4d5e6f7g8h9i0j"
                      rows="3"
                      style={{
                        fontFamily: 'monospace',
                        fontSize: '0.9rem',
                        resize: 'vertical',
                        width: '100%'
                      }}
                      required
                    />
                  </div>
                </div>

              <div className="form-actions">
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={loadingAP}
                >
                  {loadingAP ? 'Saving...' : 'Apply AP Configuration'}
                </button>
              </div>
            </form>
          </Modal>

          {/* AR Invoices Folder Modal */}
          <Modal
            isOpen={arFolderModalOpen}
            onClose={() => setArFolderModalOpen(false)}
            title="AR Invoices Folder Configuration"
          >
            <form onSubmit={handleSaveAR}>
                <div className="form-section">
                  <div className="form-group">
                    <label htmlFor="google_drive_ar_folder_id">
                      <FolderIcon style={{ width: 18, height: 18, display: 'inline', marginRight: 8 }} />
                      AR Invoices Folder ID
                    </label>
                    <small className="form-text" style={{ display: 'block', marginBottom: '12px' }}>
                      Paste the Google Drive folder ID where AR invoice documents will be stored.
                      You can find this ID in the folder's URL after /folders/
                    </small>
                    <textarea
                      id="google_drive_ar_folder_id"
                      name="google_drive_ar_folder_id"
                      className="form-control"
                      value={settings.google_drive_ar_folder_id}
                      onChange={handleInputChange}
                      placeholder="e.g., 9i8h7g6f5e4d3c2b1a0j"
                      rows="3"
                      style={{
                        fontFamily: 'monospace',
                        fontSize: '0.9rem',
                        resize: 'vertical',
                        width: '100%'
                      }}
                      required
                    />
                  </div>
                </div>

              <div className="form-actions">
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={loadingAR}
                >
                  {loadingAR ? 'Saving...' : 'Apply AR Configuration'}
                </button>
              </div>
            </form>
          </Modal>

          {/* Customer Contracts Folder Modal */}
          <Modal
            isOpen={customerContractsFolderModalOpen}
            onClose={() => setCustomerContractsFolderModalOpen(false)}
            title="Customer Contracts Folder Configuration"
          >
            <form onSubmit={handleSaveCustomerContracts}>
                <div className="form-section">
                  <div className="form-group">
                    <label htmlFor="google_drive_customer_contracts_folder_id">
                      <FolderIcon style={{ width: 18, height: 18, display: 'inline', marginRight: 8 }} />
                      Customer Contracts Folder ID
                    </label>
                    <small className="form-text" style={{ display: 'block', marginBottom: '12px' }}>
                      Paste the Google Drive folder ID where customer contract documents will be stored.
                      A subfolder will be created for each customer using their company name.
                    </small>
                    <textarea
                      id="google_drive_customer_contracts_folder_id"
                      name="google_drive_customer_contracts_folder_id"
                      className="form-control"
                      value={settings.google_drive_customer_contracts_folder_id}
                      onChange={handleInputChange}
                      placeholder="e.g., 1a2b3c4d5e6f7g8h9i0j"
                      rows="3"
                      style={{
                        fontFamily: 'monospace',
                        fontSize: '0.9rem',
                        resize: 'vertical',
                        width: '100%'
                      }}
                      required
                    />
                  </div>
                </div>

              <div className="form-actions">
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={loadingCustomerContracts}
                >
                  {loadingCustomerContracts ? 'Saving...' : 'Apply Configuration'}
                </button>
              </div>
            </form>
          </Modal>

          {/* Setup Instructions */}
          <div className="card" style={{ marginTop: '24px' }}>
            <div className="card-header">
              <InformationCircleIcon style={{ width: 24, height: 24, display: 'inline', marginRight: 8 }} />
              <h2 style={{ display: 'inline' }}>Google Workspace Setup Instructions</h2>
            </div>
            <div className="card-body">
              <div className="instructions-section">
                <h3>
                  <DocumentTextIcon style={{ width: 20, height: 20, display: 'inline', marginRight: 8 }} />
                  Step 1: Create a Google Cloud Project
                </h3>
                <ol>
                  <li>Go to <a href="https://console.cloud.google.com" target="_blank" rel="noopener noreferrer">Google Cloud Console</a></li>
                  <li>Create a new project or select an existing one</li>
                  <li>Enable the <strong>Google Drive API</strong> for your project</li>
                </ol>

                <h3 style={{ marginTop: '24px' }}>
                  <DocumentTextIcon style={{ width: 20, height: 20, display: 'inline', marginRight: 8 }} />
                  Step 2: Create a Service Account
                </h3>
                <ol>
                  <li>Navigate to <strong>IAM & Admin</strong> → <strong>Service Accounts</strong></li>
                  <li>Click <strong>Create Service Account</strong></li>
                  <li>Provide a name (e.g., "Invoice Storage Service")</li>
                  <li>Grant the service account the <strong>Editor</strong> role (optional, for basic access)</li>
                  <li>Click <strong>Done</strong></li>
                </ol>

                <h3 style={{ marginTop: '24px' }}>
                  <DocumentTextIcon style={{ width: 20, height: 20, display: 'inline', marginRight: 8 }} />
                  Step 3: Generate and Configure Service Account Key
                </h3>
                <ol>
                  <li>Click on the newly created service account</li>
                  <li>Go to the <strong>Keys</strong> tab</li>
                  <li>Click <strong>Add Key</strong> → <strong>Create new key</strong></li>
                  <li>Select <strong>JSON</strong> format and click <strong>Create</strong></li>
                  <li>Save the downloaded JSON file securely</li>
                  <li>Open the JSON file and copy the <code>client_email</code> value</li>
                  <li>Paste it in the <strong>Service Account Email</strong> field above</li>
                  <li>Copy the entire <code>private_key</code> value from the JSON file (including the BEGIN and END lines)</li>
                  <li>Paste it in the <strong>Private Key</strong> field above</li>
                  <li>Click <strong>Apply Service Account Credentials</strong> to save</li>
                </ol>

                <h3 style={{ marginTop: '24px' }}>
                  <FolderIcon style={{ width: 20, height: 20, display: 'inline', marginRight: 8 }} />
                  Step 4: Create Google Drive Folders
                </h3>
                <ol>
                  <li>In your Google Workspace Drive, create two folders:
                    <ul>
                      <li><strong>AP Invoices</strong> - For accounts payable documents</li>
                      <li><strong>AR Invoices</strong> - For accounts receivable documents</li>
                    </ul>
                  </li>
                  <li>Right-click each folder → <strong>Share</strong></li>
                  <li>Add the service account email address (from the JSON key file: <code>client_email</code>)</li>
                  <li>Grant <strong>Editor</strong> permission to the service account</li>
                  <li>Copy the folder ID from the URL (the long string after <code>/folders/</code>)</li>
                  <li>Paste the folder IDs in the fields above</li>
                </ol>

                <h3 style={{ marginTop: '24px' }}>
                  <CheckCircleIcon style={{ width: 20, height: 20, display: 'inline', marginRight: 8 }} />
                  Step 5: Verify Configuration
                </h3>
                <ol>
                  <li>Ensure all credentials are configured in the <strong>Service Account Credentials</strong> section above</li>
                  <li>Verify that both AP and AR folder IDs are set in their respective sections</li>
                  <li>Try uploading a test invoice to verify the integration works</li>
                  <li>Check the browser console and backend logs for any errors</li>
                </ol>


                <div className="alert alert-info" style={{ marginTop: '24px' }}>
                  <InformationCircleIcon style={{ width: 20, height: 20, display: 'inline', marginRight: 8 }} />
                  <strong>Important for Google Workspace:</strong> Make sure your Google Workspace admin has enabled
                  API access for your organization. Go to <strong>Admin Console</strong> → <strong>Security</strong> →
                  <strong>API Controls</strong> and ensure that "Enable API access" is turned on.
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* API Documentation */}
      {activeTab === 'api-docs' && (
        <div className="settings-content">
          <ApiDocumentation />
        </div>
      )}

      {/* Access Management */}
      {activeTab === 'access-management' && (
        <div className="settings-content">
          <AccessManagement />
        </div>
      )}
    </div>
  );
};

export default Settings;
